import { useEffect } from "react";
import {
  LayoutDashboard,
  BarChart3,
  TrendingUp,
  Percent,
  Sparkles,
} from "lucide-react";
import { PageSection } from "@/components/ui/page-section";
import { PageJumpNav, type PageJumpLink } from "@/components/ui/page-jump-nav";
import { UnifiedFinancialOverview } from "@/components/dashboard/unified-financial-overview";
import { SpendingDistribution } from "@/components/dashboard/spending-distribution";
import { SettlementRateCard } from "@/components/dashboard/settlement-rate-card";
import { PeriodSelector } from "@/components/dashboard/period-selector";
import { TransactionVolumeChart } from "@/components/dashboard/transaction-volume-chart";
import { PointsChart } from "@/components/dashboard/points-chart";
import { useAppContext } from "@/providers";
import { useDashboardData } from "@/hooks/use-dashboard-data";
import { useTransactions } from "@/hooks/use-transactions";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ErrorMessage } from "@/components/ui/error-message";
import { t } from "@/lib/i18n/messages";
import {
  DashboardPeriodProvider,
  useDashboardPeriod,
} from "@/context/dashboard-period-context";

// ── In-page jump nav links for Analytics ───────────────────────────────────

const ANALYTICS_NAV_LINKS: PageJumpLink[] = [
  { id: "overview", num: "01", ar: "النظرة المالية", en: "Overview" },
  { id: "distribution", num: "02", ar: "توزيع المصروفات والتسوية", en: "Distribution & Settlement" },
  { id: "timeline", num: "03", ar: "السلاسل الزمنية", en: "Timeline" },
];

function AnalyticsPageContent() {
  const { locale, theme } = useAppContext();
  const isRTL = locale === "ar";
  const isDark = theme === "dark";

  const {
    walletBalance: data,
    loading,
    walletError: error,
    refetch,
  } = useDashboardData();

  // Period context — keeps chart queries aligned with selector
  const { resolvedDateFrom, resolvedDateTo } = useDashboardPeriod();

  const {
    data: transactions,
    loading: txLoading,
    setDateRange,
  } = useTransactions({ initialPerPage: 200 });

  useEffect(() => {
    setDateRange(resolvedDateFrom, resolvedDateTo);
  }, [resolvedDateFrom, resolvedDateTo, setDateRange]);

  return (
    <>
      {/* ─── Hero Header ─────────────────────────────────────────────── */}
      <section className="w-full border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="p-1.5 rounded-lg bg-primary/10 text-primary">
                <Sparkles className="w-4 h-4" />
              </span>
              <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                {isRTL ? "لوحة الإحصائيات الذكية" : "Analytics & Intelligence"}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              {isRTL ? "التحليلات ومؤشرات الأداء" : "Analytics & Performance"}
            </h1>
            <p className="text-sm text-foreground-secondary mt-1">
              {isRTL
                ? "تتبع حركة النقاط وتوزيع المصاريف ونمو المبادرات عبر الفترات الزمنية."
                : "Monitor point movement, spend allocation, and initiative impact over time."}
            </p>
          </div>
          <div className="flex items-center">
            <PeriodSelector locale={locale} />
          </div>
        </div>
      </section>

      {/* ─── Sticky in-page jump nav ──────────────────────────────────── */}
      <PageJumpNav isRTL={isRTL} links={ANALYTICS_NAV_LINKS} />

      {/* ─── Body Sections ───────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-12 space-y-12 md:space-y-16">
        {loading && !data && (
          <div className="my-8">
            <LoadingSpinner size="lg" label={t(locale, "loading")} />
          </div>
        )}

        {error && !data && (
          <div className="my-8">
            <ErrorMessage
              title={t(locale, "error")}
              message={error}
              onRetry={refetch}
              retryLabel={t(locale, "retry")}
            />
          </div>
        )}

        {/* ── 01 · OVERVIEW ────────────────────────────────────────── */}
        {data && (
          <PageSection
            id="overview"
            num="01"
            icon={LayoutDashboard}
            title={isRTL ? "النظرة المالية والسيولة" : "Financial Overview"}
            subtitle={
              isRTL
                ? "مقارنة متكاملة بين الوارد والمصروفات وصافي التدفق (يومي، أسبوعي، شهري)."
                : "Integrated cashflow comparison across daily, weekly, and monthly intervals."
            }
          >
            <UnifiedFinancialOverview
              overview={data.overview}
              locale={locale}
              isDark={isDark}
            />
          </PageSection>
        )}

        {/* ── 02 · SPENDING DISTRIBUTION & SETTLEMENT ──────────────── */}
        {data && (
          <PageSection
            id="distribution"
            num="02"
            icon={BarChart3}
            title={isRTL ? "توزيع المصروفات ونسبة التسوية" : "Spending Distribution & Settlement"}
            subtitle={
              isRTL
                ? "تحليل حصة كل مبادرة من الإنفاق مع مؤشر اكتمال وتسوية العمليات المالية."
                : "Initiative spend allocation alongside overall transaction settlement performance."
            }
          >
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
              <div className="xl:col-span-8">
                <SpendingDistribution
                  distribution={data.overview.spending_distribution}
                  locale={locale}
                  isDark={isDark}
                />
              </div>
              <div className="xl:col-span-4">
                <SettlementRateCard
                  locale={locale}
                  transactions={transactions}
                  loading={txLoading}
                />
              </div>
            </div>
          </PageSection>
        )}

        {/* ── 03 · TIMELINE ─────────────────────────────────────────── */}
        <PageSection
          id="timeline"
          num="03"
          icon={TrendingUp}
          title={isRTL ? "السلاسل الزمنية" : "Timeline"}
          subtitle={
            isRTL
              ? "حجم المعاملات وحركة النقاط عبر الفترة المختارة."
              : "Transaction volume and point flow across the period."
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TransactionVolumeChart
              locale={locale}
              transactions={transactions}
              loading={txLoading}
            />
            <PointsChart
              locale={locale}
              transactions={transactions}
              loading={txLoading}
            />
          </div>
        </PageSection>


      </div>
    </>
  );
}

export default function AnalyticsPage() {
  return (
    <DashboardPeriodProvider>
      <AnalyticsPageContent />
    </DashboardPeriodProvider>
  );
}
